/**
 * 充值订单列表（Alice 页面 orders 的复刻）：三张指标卡、筛选栏、合并订单表、同步与退款。
 *
 * 背景：数据来自支付服务 /billing/admin/recharge-orders：Stripe 订单加控制面里非支付服务写入的余额变动
 *   （管理员调账、激活码到账）按时间合并。Alice 的微信渠道在本仓没有，渠道只有 Stripe 银行卡、管理员调整、激活码；
 *   「入口」列对应订单的下单入口（orders.source，本仓只有控制台），Alice 的 alice/xueai/web 入口没有对应物。
 * 设计意图：
 *   - 筛选值先放草稿，点「筛选」或回车才查询；下拉改动立即查询（与 Alice 一致）。翻页不丢条件。
 *   - 「同步 Stripe」只对待支付订单开放（回查 Stripe 并推进状态，support 即可）；「退款」只对 admin 且还有可退余额的已支付订单开放。
 *   - 余额变动流水（kind=grant）不是真实收款，金额列显示花瓣并标出，不提供同步与退款。
 * 关键约束：合并列表翻页最多到第 1000 条（后端 page_too_deep）；账号服务不可用时邮箱显示不出来，顶部提示。
 */
import { useCallback, useState } from 'react'
import { usersApi, type RechargeQuery, type RechargeRow } from '@ap/api-client/admin-users'
import { fmtMinor } from '@ap/api-client/money'
import { Link, fmtDateTime, navigate, useToast } from '@ap/ui'
import { useAdmin } from '../../session.tsx'
import { OpsNote, OpsPage, Pager, Stat, Stats, TableWrap, Toolbar } from '../kit.tsx'
import { EmptyRow, PILL, Pill, ToolInput, ToolSelect, type PillTone } from '../kit-runtime2.tsx'
import { useQuery, useU } from '../kit-users.tsx'
import { RefundModal } from './RefundModal.tsx'

const PAGE = 20

interface Filters {
  q: string
  channel: string
  status: string
  source: string
  refund: string
}
const EMPTY: Filters = { q: '', channel: '', status: '', source: '', refund: '' }

const PAY_TONE: Record<string, PillTone> = { paid: PILL.green, pending: PILL.amber, closed: PILL.gray }
const REFUND_TONE: Record<string, PillTone> = { partial: PILL.amber, refunded: PILL.red, refunding: PILL.blue }

export function OrdersList({ children }: { children?: React.ReactNode }) {
  const { t, G, err, tag, pet, num, locale } = useU()
  const O = G.orders
  const toast = useToast()
  const { isAdmin } = useAdmin()
  const [draft, setDraft] = useState<Filters>(EMPTY)
  const [applied, setApplied] = useState<Filters>(EMPTY)
  const [offset, setOffset] = useState(0)
  const [refunding, setRefunding] = useState<string | null>(null)
  const [syncing, setSyncing] = useState<string | null>(null)

  const query: RechargeQuery = {
    q: applied.q,
    pay_channel: applied.channel,
    pay_status: applied.status,
    order_source: applied.source,
    refund_status: applied.refund,
    limit: PAGE,
    offset,
  }
  const list = useQuery(() => usersApi.rechargeOrders(query), [applied, offset])

  const apply = useCallback((next: Filters) => {
    setDraft(next)
    setApplied(next)
    setOffset(0)
  }, [])
  const setField = (patch: Partial<Filters>, immediate = false) => {
    const next = { ...draft, ...patch }
    if (immediate) apply(next)
    else setDraft(next)
  }

  const sync = async (orderId: string) => {
    setSyncing(orderId)
    try {
      await usersApi.syncOrder(orderId)
      toast.ok(t(O.synced))
      list.reload()
    } catch (e) {
      toast.error(err(e))
    } finally {
      setSyncing(null)
    }
  }

  const s = list.data?.summary
  const money = (field: 'net_minor' | 'refunded_minor') =>
    s ? (s.by_currency.length ? s.by_currency.map((c) => fmtMinor(c[field], c.currency, tag)).join(' / ') : fmtMinor(0, 'USD', tag)) : '—'
  const rows = list.data?.data ?? []
  const total = list.data?.total ?? 0

  const channelOptions = [
    { value: '', label: t(O.channelAll) },
    { value: 'stripe', label: t(O.channelStripe) },
    { value: 'admin', label: t(O.channelAdmin) },
    { value: 'activation', label: t(O.channelActivation) },
  ]
  const refundLabel = (r: RechargeRow) => {
    const text = { none: O.refundNone, partial: O.refundPartial, refunded: O.refundFull, refunding: O.refundPending }[r.refund_status]
    return r.refund_status === 'none' ? (
      <span style={{ color: 'var(--text-tertiary)' }}>—</span>
    ) : (
      <>
        <Pill tone={REFUND_TONE[r.refund_status]}>{t(text)}</Pill>
        {r.refunded_minor > 0 && <div className="u2-note" style={{ marginTop: 2 }}>{t(O.refundedOf, { v: fmtMinor(r.refunded_minor, r.currency, tag) })}</div>}
      </>
    )
  }

  return (
    <OpsPage eyebrow={t(O.eyebrow)} title={t(O.title)} desc={t(O.desc)} chip={t(O.chip)}>
      <Stats cols={3}>
        <Stat label={t(O.statPaid)} value={money('net_minor')} color="success" sub={s ? t(O.paidSub, { n: s.paid }) : undefined} />
        <Stat label={t(O.statPending)} value={s ? s.pending.toLocaleString(tag) : '—'} />
        <Stat label={t(O.statRefunded)} value={money('refunded_minor')} color="error" sub={s ? t(O.refundedSub, { n: s.with_refund }) : undefined} />
      </Stats>

      <Toolbar count={list.data ? t(O.total, { n: total.toLocaleString(tag) }) : undefined}>
        <ToolInput width={240} placeholder={t(O.searchPh)} value={draft.q} onChange={(e) => setField({ q: e.target.value })} onEnter={() => apply(draft)} />
        <ToolSelect title={t(O.channelTip)} value={draft.channel} onChange={(e) => setField({ channel: e.target.value }, true)} options={channelOptions} />
        <ToolSelect
          value={draft.status}
          onChange={(e) => setField({ status: e.target.value }, true)}
          options={[
            { value: '', label: t(O.statusAll) },
            { value: 'paid', label: t(O.statusPaid) },
            { value: 'pending', label: t(O.statusPending) },
            { value: 'closed', label: t(O.statusClosed) },
          ]}
        />
        <ToolSelect
          title={t(O.sourceTip)}
          value={draft.source}
          onChange={(e) => setField({ source: e.target.value }, true)}
          options={[
            { value: '', label: t(O.sourceAll) },
            { value: 'console', label: t(O.sourceConsole) },
            { value: 'other', label: t(O.sourceOther) },
          ]}
        />
        <ToolSelect
          value={draft.refund}
          onChange={(e) => setField({ refund: e.target.value }, true)}
          options={[
            { value: '', label: t(O.refundAll) },
            { value: 'none', label: t(O.refundNone) },
            { value: 'partial', label: t(O.refundPartial) },
            { value: 'refunded', label: t(O.refundFull) },
            { value: 'refunding', label: t(O.refundPending) },
          ]}
        />
        <button className="btn btn-secondary btn-sm" onClick={() => apply(draft)}>
          {t(G.common.filter)}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => apply(EMPTY)}>
          {t(G.common.clear)}
        </button>
      </Toolbar>
      <OpsNote>{t(O.note)}</OpsNote>
      {list.data?.user_lookup_failed && <OpsNote>{t(O.lookupFailed)}</OpsNote>}

      <TableWrap
        head={
          <>
            <th>{t(O.colTime)}</th>
            <th>{t(O.colUser)}</th>
            <th>{t(O.colOrder)}</th>
            <th>{t(O.colAmount)}</th>
            <th>{t(O.colChannel)}</th>
            <th>{t(O.colSource)}</th>
            <th>{t(O.colStatus)}</th>
            <th>{t(O.colTxn)}</th>
            <th>{t(O.colRefund)}</th>
            <th>{t(O.colActions)}</th>
          </>
        }
        pager={<Pager offset={offset} limit={PAGE} total={total} onChange={setOffset} />}
      >
        {list.error != null && <EmptyRow cols={10}>{t(G.common.loadFailed, { msg: err(list.error) })}</EmptyRow>}
        {list.loading && !list.data && <EmptyRow cols={10}>{t(G.common.loading)}</EmptyRow>}
        {list.data && rows.length === 0 && <EmptyRow cols={10}>{t(O.empty)}</EmptyRow>}
        {rows.map((r) => {
          const isOrder = r.kind === 'order' && !!r.order_id
          const grantKind = O.grantKinds[r.status]
          const canRefund = isAdmin && isOrder && r.pay_status === 'paid' && r.refund_status !== 'refunded' && r.refunded_minor < r.amount_minor
          return (
            <tr key={`${r.kind}-${r.id}`}>
              <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at, locale)}</td>
              <td>
                <div style={{ fontWeight: 600 }}>{r.display_name || r.email || '—'}</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                  {r.account_id ? (
                    <Link href={`/accounts?user=${r.account_id}`} title={r.account_id}>
                      {r.email || r.account_id.slice(0, 8)}
                    </Link>
                  ) : (
                    <Link href={`/accounts/${r.account_id}`}>{r.account_id.slice(0, 8)}</Link>
                  )}
                </div>
              </td>
              <td>
                {isOrder ? (
                  <Link href={`/payments/orders/${r.order_id}`} className="mono" title={r.order_id ?? ''}>
                    {(r.order_id ?? '').slice(0, 8)}
                  </Link>
                ) : (
                  <span className="mono" title={r.id}>
                    {r.id.slice(0, 8)}
                  </span>
                )}
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{isOrder ? r.sku : grantKind ? t(grantKind) : r.status}</div>
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {isOrder ? (
                  <>
                    <div style={{ fontWeight: 600 }}>{fmtMinor(r.amount_minor, r.currency, tag)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                      {t(O.petalsPaid, { n: num(r.credit_units) })}
                      {r.bonus_units > 0 && ` · ${t(O.petalsBonus, { n: num(r.bonus_units) })}`}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontWeight: 600 }}>{pet(r.credit_units + r.bonus_units)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t(O.notRealPayment)}</div>
                  </>
                )}
              </td>
              <td>{O.channelNames[r.pay_channel] ? t(O.channelNames[r.pay_channel]) : r.pay_channel}</td>
              <td>{isOrder ? (r.order_source === 'console' ? t(O.sourceConsole) : r.order_source || '—') : '—'}</td>
              <td>
                <Pill tone={PAY_TONE[r.pay_status] ?? PILL.gray}>
                  {t({ paid: O.statusPaid, pending: O.statusPending, closed: O.statusClosed }[r.pay_status] ?? O.statusClosed)}
                </Pill>
              </td>
              <td className="mono" style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.channel_txn_id}>
                {r.channel_txn_id || '—'}
              </td>
              <td>{refundLabel(r)}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {isOrder && (
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/payments/orders/${r.order_id}`)}>
                      {t(O.detailBtn)}
                    </button>
                  )}
                  {isOrder && r.pay_status === 'pending' && (
                    <button className="btn btn-secondary btn-sm" disabled={syncing === r.order_id} onClick={() => sync(r.order_id as string)}>
                      {t(O.syncBtn)}
                    </button>
                  )}
                  {canRefund && (
                    <button className="btn btn-danger btn-sm" onClick={() => setRefunding(r.order_id)}>
                      {t(O.refundBtn)}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          )
        })}
      </TableWrap>
      {children}
      <RefundModal orderId={refunding} onClose={() => setRefunding(null)} onDone={list.reload} />
    </OpsPage>
  )
}
